import { useCallback } from "react";
import mixpanel from "mixpanel-browser";
import { useNavigate } from "react-router-dom";
import { isChatVisible } from "../scope/visible-chats";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  getCompletedDraftAcknowledgement,
  draftRoutePath,
  getChatKey,
  isDraftChatKey,
  hasCompletedDraft,
  hasIdentifiedCompletedDraft,
  PLAYGROUND_STREAM_KEY,
  streamingUtils,
  useStreamingStore,
} from "../store/streaming-store";
import { useToast } from "@/features/toast";
import { QueryKeys } from "@/api/config/query-keys";
import { clientSource } from "@/lib/client-source";
import { MessageTransformer } from "@/api/services/message-transformer";
import type {
  BackendConversationMessagesResponse,
  BackendInfiniteScrollResponse,
} from "@/api/models/api-types";
import type { Message } from "@/types";
import {
  cancelRedirect,
  flushRedirect,
  requestCartNavigation,
  requestNavigation,
  resolveStreamRedirection,
} from "../redirect-navigation";
import { useCartStore } from "@/features/cart/store/cart-store";
import { handlePaymentRequiredMiddleware } from "@/api/middleware/payment-required-middleware";
import useAuthStore from "@/store/use-auth-store";
import { SSEStreamDecoder, type SSEEvent } from "../utils/sse-stream";
import { useChatScope } from "../scope/chat-scope-context";
import { getModuleChatConfig, moduleRegistry, type ModuleId } from "@/modules";
import { useChatDockStore } from "@/features/chat-dock/store/chat-dock-store";
import { useChatStore } from "../store/chat-store";
import { useLLMSelection } from "@/features/llm-selection";
import type { ChatAttachment } from "../types";
import {
  parseExternalChatLink,
  parseWebSearchProducts,
} from "@/api/utils/chat-artifacts";
import { parseProductComparison } from "@/features/product-comparison/model";

// ===== Constants =====
// Stream tamamlandıktan sonra backend'in mesajı persist etmesini beklerken
// kullanılan artan aralıklı refetch denemeleri. Draft ack'lendiğinde
// (backend kopyası cache'e düştüğünde) kalan denemeler iptal edilir.
const RECONCILE_DELAYS_MS = [1000, 2000, 4000, 8000] as const;

/** Taslak tur konuşmaya bağlandıysa kuyruk bu id ile boşalır (taslak anahtarı → id). */
const adoptedDraftIds = new Map<string, string>();

/** İlk tur bağlanmadan bitti: taslağı bırak, metni composer'a geri koy. */
const abandonDraftTurn = (
  draftKey: string,
  message: string,
  attachments: ChatAttachment[] | undefined,
  navigate: (to: string, opts?: { replace?: boolean }) => void,
) => {
  useStreamingStore.getState().resetSession(draftKey);
  const dock = useChatDockStore.getState();
  let clearedDock = false;
  for (const [moduleId, id] of Object.entries(dock.chatByModule)) {
    if (id === draftKey) {
      dock.setModuleChat(moduleId as ModuleId, null);
      clearedDock = true;
    }
  }
  if (clearedDock) {
    dock.setComposerRestore({ message, attachments });
  }
  // Kullanıcı başka bir sayfaya geçtiyse oradaki composer'a dokunma
  if (window.location.pathname === draftRoutePath(draftKey)) {
    useChatStore.getState().setMessage(message);
    if (attachments?.length) {
      useChatStore.getState().setSelectedAttachments(attachments);
    }
    navigate("/", { replace: true });
  }
};

/** Query cache'teki normal/infinite history şekillerini tek mesaj listesine açar. */
const getCachedHistoryMessages = (cachedValues: unknown[]): Message[] => {
  const byId = new Map<string, Message>();

  cachedValues.forEach((value) => {
    if (typeof value !== "object" || value === null) return;

    const infinite = value as { pages?: BackendInfiniteScrollResponse[] };
    if (Array.isArray(infinite.pages)) {
      [...infinite.pages].reverse().forEach((page) => {
        MessageTransformer.transformInfiniteScrollToMessages(page).forEach(
          (message) => byId.set(message.id, message),
        );
      });
      return;
    }

    const direct = value as BackendConversationMessagesResponse;
    if (Array.isArray(direct.data?.chat)) {
      MessageTransformer.transformConversationToMessages(direct).forEach(
        (message) => byId.set(message.id, message),
      );
    }
  });

  return [...byId.values()].sort(
    (left, right) =>
      new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime(),
  );
};

// Bir chat için aynı anda yalnız bir FIFO drain/reconciliation sahibi çalışır.
// Hook'u kullanan composer, message listesi ve public sender aynı Promise'i
// paylaşır; retry bütçesi ve toast chat başına tek kalır.
const queueDrainOwners = new Map<string, Promise<void>>();

// ===== Streaming Chat Hook =====
export const useStreamingChat = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { error: errorToast, info: infoToast } = useToast();
  // Dock'ta çalışıyorsak konuşmanın modülü; tam sayfada/wizard'da yok
  const scopeModuleId = useChatScope()?.module;
  const navigate = useNavigate();
  const { selectedModel } = useLLMSelection();

  // Store actions (per-chat session based)
  const ensureSession = useStreamingStore((state) => state.ensureSession);
  const startStreaming = useStreamingStore((state) => state.startStreaming);
  const setServerMessageId = useStreamingStore(
    (state) => state.setServerMessageId,
  );
  const updateStreamingContent = useStreamingStore(
    (state) => state.updateStreamingContent,
  );
  const addStatusMessage = useStreamingStore((state) => state.addStatusMessage);
  const addReasoningMessage = useStreamingStore(
    (state) => state.addReasoningMessage,
  );
  const addExternalLink = useStreamingStore((state) => state.addExternalLink);
  const setComparison = useStreamingStore((state) => state.setComparison);
  const setWebProducts = useStreamingStore((state) => state.setWebProducts);
  const completeStreaming = useStreamingStore(
    (state) => state.completeStreaming,
  );
  const failStreaming = useStreamingStore((state) => state.failStreaming);
  const setConnectionState = useStreamingStore(
    (state) => state.setConnectionState,
  );
  const abortStreaming = useStreamingStore((state) => state.abortStreaming);
  const stopOwnedStreaming = useStreamingStore((state) => state.stopStreaming);
  const resetSession = useStreamingStore((state) => state.resetSession);
  const setModelSuggestions = useStreamingStore(
    (state) => state.setModelSuggestions,
  );
  const setModelList = useStreamingStore((state) => state.setModelList);

  // ===== Gönderim hatası yan etkileri (toast + error state) =====
  // Hem ilk gönderim hem kuyruktan otomatik gönderim aynı işleyişi kullanır.
  const handleSendFailure = useCallback(
    (chatKey: string, sendError: unknown, streamId: string) => {
      // "handled" hatalar (backend error event'i, 402 payment) zaten kendi
      // yerlerinde toast/error-state üretti; burada tekrar işlem yapma.
      const isHandled =
        (sendError as { handled?: boolean } | null)?.handled === true;
      if (isHandled) return;

      // Sahibi değiliz: bu hata artık geçerli olmayan bir kuşağa ait. Yeni tur
      // sürerken eski turun hatasını göstermek kullanıcıyı yanıltır.
      if (
        useStreamingStore.getState().getSession(chatKey).streamId !== streamId
      ) {
        return;
      }

      // Buraya yalnızca "handled" olmayan hatalar düşer: fetch/reader
      // seviyesindeki taşıma hataları. Bunların mesajı bir üretici sözleşmesi
      // değil, transport ayrıntısı — `HTTP error! status: 500`, `Failed to
      // fetch`, `No response body reader available` gibi. Kullanıcıya
      // gösterilmez ve store'a yazılmaz; sabit, çevrilmiş metin kullanılır.
      // (Backend'in SSE `error` event'inden gelen mesaj bilinçli olarak
      // korunuyor — o "handled" yoluyla kendi yerinde işleniyor.)
      const errorMessage = t("chat.errors.stream");

      const existingError = useStreamingStore
        .getState()
        .getSession(chatKey).streamingError;
      if (existingError) return;

      // Playground (wizard) hatalarını Result alanı gösterir, toast açılmaz
      if (chatKey !== PLAYGROUND_STREAM_KEY) {
        errorToast(errorMessage, {
          title: t("common.error"),
        });
      }

      failStreaming(
        chatKey,
        {
          message: errorMessage,
          code: "STREAMING_ERROR",
          timestamp: Date.now(),
        },
        streamId,
      );

      // Ham ayrıntı yalnızca konsolda kalır: teşhis için gerekli, kullanıcı
      // arayüzü ve store için değil.
      console.error("Streaming send failure:", sendError);
    },
    [t, errorToast, failStreaming],
  );

  // ===== Send Streaming Request =====
  const sendStreamingRequest = useCallback(
    async (
      message: string,
      chatId: string | null | undefined,
      model: string | undefined,
      streamId: string,
      attachments?: ChatAttachment[],
      playground?: boolean,
      abortController?: AbortController,
    ): Promise<void> => {
      const isFirstDraft = isDraftChatKey(chatId);
      const draftKey = isFirstDraft ? chatId : "";
      let chatKey = isFirstDraft ? chatId : getChatKey(chatId);
      let boundChatId = isFirstDraft ? "" : (chatId ?? "").trim();

      /**
       * Bu okuma döngüsü hâlâ oturumun sahibi mi?
       *
       * Aynı sohbette yeni bir tur başladıysa (kullanıcı ikinci mesajı
       * gönderdi, sohbet sıfırlandı, silindi) eski döngü ağdan veri almaya
       * devam edebiliyor. Sahiplik düştüğü anda yazmayı bırakıp okumayı
       * sonlandırıyoruz.
       */
      const ownsSession = (): boolean =>
        useStreamingStore.getState().getSession(chatKey).streamId === streamId;

      // Get base URL from environment
      const baseUrl =
        import.meta.env.VITE_AI_API_BASE_URL || "https://api.chatharmony.ai";
      const apiUrl = `${baseUrl}/api/v1/ai/chat`;

      // ChatAttachment → backend attachment payload'ı
      const attachmentsPayload = (attachments ?? []).map((attachment) => ({
        url: attachment.url,
        fileName: attachment.name,
        fileType: attachment.mimeType,
        fileSize: attachment.size.toString(),
      }));

      // Dock'tan konuşuyorsak modülü ve ajanını bildir. Backend bunu yeni ya
      // da modülsüz konuşmaya damgalar (saklanan modül her zaman kazanır);
      // `agentId` şimdilik yalnızca kayıt, yönlendirme kararı vermiyor.
      const moduleId = scopeModuleId;
      const moduleAgentId = moduleId
        ? getModuleChatConfig(moduleRegistry.getModuleById(moduleId))?.agentId
        : undefined;

      // Create request payload
      const payload: any = {
        message,
        stream: true,
        ...(model && { model }),
        // Taslak ilk tur ve wizard chatId taşımaz. Sonraki mesajlar bound id ile gider.
        ...(boundChatId && { chatId: boundChatId }),
        ...(moduleId && { module: moduleId }),
        ...(moduleAgentId && { agentId: moduleAgentId }),
        ...(attachmentsPayload.length > 0 && {
          attachments: attachmentsPayload,
        }),
        ...(playground && { playground: true }),
      };

      // Get auth token (persist edilen localStorage'ı elle parse etmek yerine
      // canlı auth store'dan oku — token refresh sonrası da günceldir)
      const authToken = useAuthStore.getState().accessToken;

      // Backend mesajı kaydettikten sonra history'yi senkronize et.
      // Sabit tek deneme yerine artan aralıklı denemeler: draft ack'lenince
      // (StreamingMessages backend kopyasını görüp draft'ı temizleyince) durur.
      const scheduleMessagesRefetch = (attempt = 0) => {
        if (!boundChatId) return;
        if (attempt >= RECONCILE_DELAYS_MS.length) return;

        // Draft ile başlayan bir senkronda draft ilk denemeden önce silindiyse
        // cache zaten backend kopyasını taşıyor (ör. ilk turda sayfa gerçek
        // id'ye geçerken çekilen history). Draft'sız başlayan senkron ise
        // (stop, boş cevap) ilk refetch'i her zaman yapar.
        const startedWithDraft =
          attempt === 0 &&
          hasCompletedDraft(useStreamingStore.getState().getSession(chatKey));

        setTimeout(() => {
          const session =
            useStreamingStore.getState().sessionsByChat[chatKey];
          // Queue varsa history reconciliation component mount'undan bağımsız
          // drain döngüsünün sahibidir; aynı dört refetch'i iki kez çalıştırma.
          if (session?.queuedMessages.length) return;

          if (attempt > 0 || startedWithDraft) {
            const hasPendingDraft = session
              ? hasCompletedDraft(session)
              : false;
            // Draft temizlendiyse backend senkronu tamamlanmıştır
            if (!hasPendingDraft) return;
          }

          void queryClient
            .invalidateQueries({
              queryKey: QueryKeys.CONVERSATIONS.messages(boundChatId),
            })
            .then(() => {
              if (attempt + 1 < RECONCILE_DELAYS_MS.length) {
                scheduleMessagesRefetch(attempt + 1);
                return;
              }

              // Son denemeden sonra yalnız gerçekten bloklanmış bir kuyruk
              // varsa görünür hata üret. Draft ve mesaj korunur; sessizce yeni
              // stream başlatılmaz.
              setTimeout(() => {
                const current = useStreamingStore
                  .getState()
                  .getSession(chatKey);
                if (
                  hasIdentifiedCompletedDraft(current) &&
                  current.queuedMessages.length > 0
                ) {
                  errorToast(t("chat.errors.historySync"), {
                    id: `chat-history-sync-${chatKey}`,
                    title: t("common.error"),
                  });
                }
              }, 0);
            });
        }, RECONCILE_DELAYS_MS[attempt]);
      };

      // Yalnız [DONE] ve compatibility complete için ortak sonlandırma.
      const finalizeCompletion = () => {
        if (!ownsSession()) return;
        // Sohbet ekranda değilse (tam sayfa ya da dock) okunmadı işaretle
        const shouldMarkUnread =
          boundChatId !== "" &&
          (document.hidden ||
            (window.location.pathname !== `/chat/${boundChatId}` &&
              !isChatVisible(boundChatId)));

        completeStreaming(
          chatKey,
          chatKey !== PLAYGROUND_STREAM_KEY ? Boolean(shouldMarkUnread) : false,
          streamId,
        );
        if (flushRedirect(chatKey, streamId) === "route") {
          infoToast(t("chat.redirecting"), { duration: 2500 });
        }
        scheduleMessagesRefetch();
      };

      try {
        // Make POST request with streaming
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-source": clientSource(),
            ...(authToken && { Authorization: authToken }),
          },
          body: JSON.stringify(payload),
          signal: abortController?.signal,
        });

        if (!ownsSession()) return;

        if (!response.ok) {
          // Gövdeyi klon üzerinden oku: hata dalında stream tüketilmiyor ama
          // orijinal response'un body'sini burada harcamak, ileride bu dala
          // eklenecek herhangi bir okumayı sessizce bozar.
          const errorBody = await response
            .clone()
            .json()
            .catch(() => undefined);

          // Response body okunurken kullanıcı stop etmiş veya aynı sohbette
          // yeni bir tur başlamış olabilir. Eski 402 yeni turu durduramaz ve
          // onun adına modal açamaz.
          if (!ownsSession()) return;

          const handled = handlePaymentRequiredMiddleware({
            status: response.status,
            source: "chat",
            body: errorBody,
          });
          if (handled) {
            // 402 gibi ödeme hataları middleware tarafından yakalandı (modal açıldı).
            // Session'ı idle'a çek; caller mesajı input'a geri koyabilsin diye
            // "handled" işaretli hata fırlat.
            abortStreaming(chatKey, streamId);
            const paymentError = new Error("PAYMENT_REQUIRED") as Error & {
              handled?: boolean;
            };
            paymentError.handled = true;
            throw paymentError;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Set connection state
        setConnectionState(chatKey, true, streamId);

        // Handle streaming response
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No response body reader available");
        }

        const textDecoder = new TextDecoder();
        const sseDecoder = new SSEStreamDecoder();
        let isComplete = false;
        let cartRefreshPromise: Promise<void> | null = null;

        // Parse edilmiş tek bir SSE event'ini işler; stream sonlanmalıysa true döner.
        const handleEvent = (event: SSEEvent): boolean => {
          // Sahiplik düştüyse (yeni tur başladı, oturum sıfırlandı, sohbet
          // silindi) bu döngü artık yazamaz; okumayı da sonlandır.
          if (!ownsSession()) return true;

          if (event.kind === "done") {
            isComplete = true;
            finalizeCompletion();
            return true;
          }

          const data: any = event.data;

          if (data.type === "error") {
            const errorMessage =
              data.content || data.message || "Streaming error occurred";
            const errorCode = data.code || "STREAMING_ERROR";
            console.error("❌ Streaming error received:", errorMessage);

            failStreaming(
              chatKey,
              {
                message: errorMessage,
                code: errorCode,
                timestamp: Date.now(),
              },
              streamId,
            );

            // Hata ile biten bir tur kullanıcıyı başka bir ekrana taşımaz.
            cancelRedirect(chatKey, streamId);

            // Playground (wizard) hatalarını Result alanı gösterir
            if (chatKey !== PLAYGROUND_STREAM_KEY) {
              errorToast(errorMessage, {
                title: t("common.error"),
              });
            }

            // Toast/error-state burada üretildi; caller'da tekrarlanmasın
            const backendError = new Error(errorMessage) as Error & {
              handled?: boolean;
            };
            backendError.handled = true;
            throw backendError;
          } else if (data.type === "initial_response") {
            // initial_response'dan gelen _id ile backend mesajı ID üzerinden reconcile edilebilir
            const serverMessageId = data.content?._id || data._id;
            if (serverMessageId) {
              setServerMessageId(chatKey, serverMessageId);
            }
          } else if (data.type === "chunk" || data.type === "token") {
            updateStreamingContent(chatKey, data.content || "");
          } else if (data.type === "status") {
            addStatusMessage(chatKey, {
              content: data.content,
              timestamp: Date.now(),
            });
          } else if (data.type === "reasoning") {
            addReasoningMessage(chatKey, {
              content: data.content,
              timestamp: Date.now(),
            });
          } else if (data.type === "cart_update") {
            // Aynı akış içinde peş peşe event gelirse tek fetch çalıştır.
            if (!cartRefreshPromise) {
              cartRefreshPromise = useCartStore
                .getState()
                .loadCart()
                .catch((cartError) => {
                  console.error(
                    "Cart refresh failed after cart_update event:",
                    cartError,
                  );
                })
                .finally(() => {
                  cartRefreshPromise = null;
                });
            }
            // "Sepeti aç" da bu event ile gelir; ayrı bir redirection yoktur.
            // Aynı turda checkout gibi bir redirection varsa o kazanır.
            requestCartNavigation(chatKey, streamId);
          } else if (data.type === "conversation_created") {
            const createdId = data.content?.chatId;
            if (
              isFirstDraft &&
              typeof createdId === "string" &&
              createdId.trim() !== "" &&
              !boundChatId
            ) {
              useStreamingStore.getState().renameSession(chatKey, createdId);
              chatKey = createdId;
              boundChatId = createdId;
              adoptedDraftIds.set(draftKey, createdId);
              const dock = useChatDockStore.getState();
              for (const [moduleId, id] of Object.entries(dock.chatByModule)) {
                if (id === draftKey) {
                  dock.setModuleChat(moduleId as ModuleId, createdId);
                }
              }
              // Kullanıcı başka sohbete geçtiyse onu geri çekme; akış arka
              // planda gerçek id'ye taşınır, sohbet listede okunmamış görünür.
              if (window.location.pathname === draftRoutePath(draftKey)) {
                navigate(`/chat/${createdId}`, { replace: true });
              }
              const userId = useAuthStore.getState().user?._id;
              queryClient.invalidateQueries({
                queryKey: [...QueryKeys.CONVERSATIONS.list, userId],
              });
              try {
                mixpanel.track("chat_started", {
                  conversation_id: createdId,
                  module: scopeModuleId ?? null,
                  model_id: selectedModel?._id,
                  model_name: model ?? selectedModel?.name,
                });
              } catch (mixpanelError) {
                console.warn("Mixpanel error:", mixpanelError);
              }
            }
          } else if (data.type === "model_suggestions") {
            if (boundChatId) {
              setModelSuggestions(data.content || [], boundChatId);
            }
          } else if (data.type === "model_list") {
            if (boundChatId) {
              setModelList(data.content || [], boundChatId);
            }
          } else if (data.type === "product_comparison") {
            const comparison = parseProductComparison(data.content);
            if (comparison) setComparison(chatKey, comparison);
          } else if (data.type === "external_link") {
            const link = parseExternalChatLink(data.content);
            if (link) {
              addExternalLink(chatKey, {
                ...link,
                timestamp: Date.now(),
              });
            }
          } else if (data.type === "web_product_cards") {
            const products = parseWebSearchProducts(data.content?.products);
            const query = data.content?.query;
            if (products.length > 0) {
              setWebProducts(
                chatKey,
                products,
                typeof query === "string" && query.trim() !== "" ? query : null,
              );
            }
          } else if (data.type === "redirection") {
            const navigation = resolveStreamRedirection(data.content);
            if (navigation) {
              requestNavigation(navigation, chatKey, streamId);
            }
          } else if (data.type === "complete") {
            isComplete = true;
            finalizeCompletion();
            return true;
          } else if (data.type === "final_response") {
            // Payload history tarafından render edilir. Shopping Agent bu
            // event'i son text token'ından önce gönderdiği için terminal
            // değildir ve okuma devam eder.
          }

          return false;
        };

        try {
          let shouldStop = false;
          while (!shouldStop) {
            const { done, value } = await reader.read();
            if (done) break;

            const events = sseDecoder.push(
              textDecoder.decode(value, { stream: true }),
            );
            for (const event of events) {
              if (handleEvent(event)) {
                shouldStop = true;
                break;
              }
            }
          }

          // Stream sonunda buffer'da newline'sız kalan son satırı da işle
          if (!shouldStop) {
            for (const event of sseDecoder.flush()) {
              if (handleEvent(event)) break;
            }
          }
        } finally {
          try {
            await reader.cancel();
          } catch {
            // stream zaten kapalı olabilir
          }
          try {
            reader.releaseLock();
          } catch {
            // lock zaten bırakılmış olabilir
          }
        }

        if (!isComplete) {
          // EOF terminal bir başarı sinyali DEĞİL: backend `[DONE]` ya da
          // `complete` yazmadan bağlantıyı kapattıysa tur
          // yarıda kesilmiştir. Ekrandaki kısmi cevabı koru (kullanıcı aldığı
          // kadarını görsün) ama turu hata ile bitir — yarım bir cevap
          // kullanıcıyı başka bir ekrana taşıyamaz ve arkasındaki kuyruğu
          // boşaltamaz.
          if (!ownsSession()) return;
          if (isFirstDraft && !boundChatId) {
            errorToast(t("chat.errors.stream"), {
              title: t("common.error"),
            });
            abandonDraftTurn(draftKey, message, attachments, navigate);
            return;
          }
          failStreaming(
            chatKey,
            {
              message: t("chat.errors.stream"),
              code: "STREAMING_ERROR",
              timestamp: Date.now(),
            },
            streamId,
            true,
          );
          cancelRedirect(chatKey, streamId);
          scheduleMessagesRefetch();
          if (chatKey !== PLAYGROUND_STREAM_KEY) {
            errorToast(t("chat.errors.stream"), {
              title: t("common.error"),
            });
          }
        }
      } catch (requestError: any) {
        // Kullanıcının kendi durdurması (stop) hata değildir:
        // kısmi içeriği draft olarak koru, toast/error-state üretme.
        if (
          !requestError?.handled &&
          (requestError?.name === "AbortError" ||
            abortController?.signal.aborted)
        ) {
          if (isFirstDraft && !boundChatId) {
            abandonDraftTurn(draftKey, message, attachments, navigate);
          } else {
            scheduleMessagesRefetch();
          }
          return;
        }

        if (!requestError?.handled) {
          console.error("Streaming request error:", requestError);
          setConnectionState(chatKey, false, streamId);
        }
        if (isFirstDraft && !boundChatId) {
          if (!requestError?.handled) {
            errorToast(t("chat.errors.stream"), {
              title: t("common.error"),
            });
          }
          abandonDraftTurn(draftKey, message, attachments, navigate);
        }
        throw requestError;
      }
    },
    [
      queryClient,
      updateStreamingContent,
      completeStreaming,
      failStreaming,
      setConnectionState,
      abortStreaming,
      errorToast,
      t,
      setServerMessageId,
      addStatusMessage,
      addReasoningMessage,
      addExternalLink,
      setComparison,
      setWebProducts,
      setModelSuggestions,
      setModelList,
      infoToast,
      scopeModuleId,
      navigate,
      selectedModel,
    ],
  );

  // ===== Tek mesajı gönder (stream başlat + bitmesini bekle) =====
  const startAndSendMessage = useCallback(
    async (
      message: string,
      chatId: string | null | undefined,
      model?: string,
      attachments?: ChatAttachment[],
      playground?: boolean,
    ) => {
      const chatKey = getChatKey(chatId);
      ensureSession(chatKey);

      // 1) Generate client message IDs
      const assistantMessageId = `streaming_${streamingUtils.generateMessageId()}`;
      const abortController = new AbortController();

      // 2) Start streaming for this chat session. Yeni tur, önceki turun
      // bekleyen yönlendirme niyetini devralmaz.
      cancelRedirect(chatKey);
      const streamId = startStreaming(
        chatKey,
        assistantMessageId,
        abortController,
      );

      // 3) Send streaming request (continues even if UI unmounts)
      // Hata yan etkileri burada üretiliyor: streamId yalnızca bu kapsamda
      // biliniyor, çağıranlara taşımak sahiplik kontrolünü dağıtırdı.
      try {
        await sendStreamingRequest(
          message,
          chatId,
          model, // Use selected model
          streamId,
          attachments, // 🆕 Attachments ekle
          playground, // 🆕 Playground mode
          abortController,
        );
      } catch (sendError: unknown) {
        handleSendFailure(chatKey, sendError, streamId);
        throw sendError;
      }
    },
    [ensureSession, startStreaming, sendStreamingRequest, handleSendFailure],
  );

  // ===== Kuyruktaki mesajları sırayla gönder =====
  // Aktif stream bittiğinde (complete/stop) kuyruğa alınmış mesajlar FIFO
  // sırayla otomatik gönderilir. Bir mesaj hata alırsa kuyruk durur; kalan
  // mesajlar kuyrukta görünür kalır ve bir sonraki gönderimle devam eder.
  const drainQueuedMessages = useCallback(
    (chatId: string | null | undefined): Promise<void> => {
      const chatKey = getChatKey(chatId);
      const existingOwner = queueDrainOwners.get(chatKey);
      if (existingOwner) return existingOwner;

      const owner = (async () => {
        let reconcileAttempt = 0;

        while (true) {
          const store = useStreamingStore.getState();
          const session = store.getSession(chatKey);
          const status = session.status;
          // Başka bir gönderim zinciri aktifse ikinci paralel stream açma
          if (status === "streaming") return;
          // Önceki tur hata ile bittiyse kuyruğu boşaltma: yarıda kesilen bir
          // cevabın arkasından sıradaki mesajı göndermek, kullanıcının görmediği
          // bir hatanın üstüne yazar. Kuyruk kalır, sonraki gönderimle devam eder.
          if (status === "error") return;
          // Widget ile aynı gate: history önceki optimistic çifti ve tüm
          // artefaktlarını karşılamadan yeni stream singleton draft'ı ezemez.
          if (hasIdentifiedCompletedDraft(session)) {
            if (!chatId || chatId.trim() === "") return;
            // Kuyruk boşken bekleyecek mesaj yok; history senkronunu
            // scheduleMessagesRefetch üstlenir (kuyruk doluyken o da kendini atlar).
            if (session.queuedMessages.length === 0) return;

            if (reconcileAttempt >= RECONCILE_DELAYS_MS.length) {
              errorToast(t("chat.errors.historySync"), {
                id: `chat-history-sync-${chatKey}`,
                title: t("common.error"),
              });
              return;
            }

            const delay = RECONCILE_DELAYS_MS[reconcileAttempt];
            reconcileAttempt += 1;
            await new Promise<void>((resolve) => setTimeout(resolve, delay));

            const latestBeforeRefetch = useStreamingStore
              .getState()
              .getSession(chatKey);
            if (!hasIdentifiedCompletedDraft(latestBeforeRefetch)) {
              reconcileAttempt = 0;
              continue;
            }

            // Default invalidation yalnız active query'yi refetch eder. `all`,
            // kullanıcı chat'ten ayrılmış olsa bile cache'teki inactive history
            // query'sini yeniler; dönen Promise fetch'ler tamamlanınca çözülür.
            const historyQueryKey = QueryKeys.CONVERSATIONS.messages(chatId);
            await queryClient.invalidateQueries({
              queryKey: historyQueryKey,
              refetchType: "all",
            });

            const latest = useStreamingStore.getState().getSession(chatKey);
            if (!hasIdentifiedCompletedDraft(latest)) {
              reconcileAttempt = 0;
              continue;
            }

            const cachedValues = queryClient
              .getQueriesData({ queryKey: historyQueryKey })
              .map(([, value]) => value);
            const acknowledgement = getCompletedDraftAcknowledgement(
              getCachedHistoryMessages(cachedValues),
              latest,
            );
            if (acknowledgement.complete) {
              useStreamingStore
                .getState()
                .clearCompletedDraft(
                  chatKey,
                  latest.completedAssistantMessageId ?? undefined,
                );
              reconcileAttempt = 0;
            }
            continue;
          }

          const next = store.dequeueMessage(chatKey);
          if (!next) return;

          // Optimistic kullanıcı balonu (manuel gönderim akışıyla aynı)
          store.setPendingUserMessage(
            chatKey,
            next.message,
            `pending_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
            next.attachmentImageUrls,
          );

          try {
            await startAndSendMessage(
              next.message,
              chatId,
              next.model,
              next.attachments.length > 0 ? next.attachments : undefined,
            );
            reconcileAttempt = 0;
          } catch {
            // Hata yan etkileri (toast + error state) startAndSendMessage içinde
            // üretildi; burada yalnızca optimistic balonu temizleyip kuyruğu
            // durduruyoruz.
            useStreamingStore
              .getState()
              .setPendingUserMessage(chatKey, null, null);
            return;
          }
        }
      })().finally(() => {
        if (queueDrainOwners.get(chatKey) === owner) {
          queueDrainOwners.delete(chatKey);
        }
      });

      queueDrainOwners.set(chatKey, owner);
      return owner;
    },
    [startAndSendMessage, queryClient, errorToast, t],
  );

  // ===== Send Streaming Message =====
  const sendStreamingMessage = useCallback(
    async (
      message: string,
      chatId: string | null | undefined,
      model?: string,
      attachments?: ChatAttachment[],
      playground?: boolean,
    ) => {
      const chatKey = getChatKey(chatId);
      const store = useStreamingStore.getState();
      const session = store.getSession(chatKey);

      // Composer dışındaki caller'lar da (simplify/regenerate gibi) aynı
      // history gate'ine tabidir. Identified draft ack'lenmeden singleton
      // completed alanlarını yeni turla ezme; isteği görünür FIFO kuyruğa al.
      if (chatId && hasIdentifiedCompletedDraft(session)) {
        store.setPendingUserMessage(chatKey, null, null);
        store.enqueueMessage(chatKey, {
          message,
          attachments: attachments ?? [],
          attachmentImageUrls: (attachments ?? [])
            .filter((attachment) => attachment.mimeType.startsWith("image/"))
            .map((attachment) => attachment.url),
          model,
        });
        await drainQueuedMessages(chatId);
        return;
      }

      // Hata yan etkileri startAndSendMessage içinde üretiliyor; burada hata
      // yalnızca yukarı geçiyor ki caller'lar (input restore, pending mesaj
      // temizliği) kendi telafi adımlarını çalıştırabilsin.
      await startAndSendMessage(message, chatId, model, attachments, playground);

      let drainId = chatId;
      if (isDraftChatKey(chatId)) {
        drainId = adoptedDraftIds.get(chatId) ?? null;
        adoptedDraftIds.delete(chatId);
        // Bağlanmadan biten taslağın kuyruğu yok; oturumu zaten bırakıldı
        if (!drainId) return;
      }

      // Stream bitti (kullanıcı stop'u dahil): sırada bekleyen mesaj varsa gönder
      await drainQueuedMessages(drainId);
    },
    [startAndSendMessage, drainQueuedMessages],
  );

  // ===== Stop Streaming =====
  const stopStreaming = useCallback(
    (chatId: string | null | undefined) => {
      const chatKey = getChatKey(chatId);
      const stoppedStreamId = stopOwnedStreaming(chatKey);
      // Turu durdurmak, o turun istediği yönlendirmeyi de durdurur: iptal
      // ettiğin bir turun seni checkout'a taşıması iptalin tam tersi olurdu.
      cancelRedirect(chatKey, stoppedStreamId ?? undefined);
    },
    [stopOwnedStreaming],
  );

  const resetStreaming = useCallback(
    (chatId: string | null | undefined) => {
      const chatKey = getChatKey(chatId);
      cancelRedirect(chatKey);
      resetSession(chatKey);
    },
    [resetSession],
  );

  // ===== Retry Streaming =====
  const retryStreaming = useCallback(
    async (
      message: string,
      chatId: string,
      model?: string,
      attachments?: ChatAttachment[],
      playground?: boolean,
    ) => {
      // Reset error state
      cancelRedirect(getChatKey(chatId));
      resetSession(getChatKey(chatId));

      // Retry the request
      return sendStreamingMessage(
        message,
        chatId,
        model,
        attachments,
        playground,
      );
    },
    [sendStreamingMessage, resetSession],
  );

  return {
    // Actions
    sendStreamingMessage,
    stopStreaming,
    retryStreaming,
    resetStreaming,
    drainQueuedMessages,
  };
};

// ===== Streaming Status Hook =====
export const useStreamingStatus = (chatId: string | null | undefined) => {
  const chatKey = getChatKey(chatId);
  const session = useStreamingStore((state) => state.sessionsByChat[chatKey]);

  const status = session?.status ?? "idle";
  const isConnected = session?.isConnected ?? false;
  const streamingError = session?.streamingError ?? null;

  return {
    isStreaming: status === "streaming",
    isConnected,
    hasError: !!streamingError,
    error: streamingError,
  };
};

export default useStreamingChat;
